import axios from "axios";
import router from "@/router";
import store from "@/store";
import * as sessionTokens from "@/auth/sessionTokens";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase/config";
import { showAlertModal } from "@/utils";
import { jwtDecode } from "jwt-decode";
import { i18n } from "./i18n";

axios.defaults.baseURL =
    process.env.VUE_APP_API_URL || "http://localhost:8000/api";

// Set VUE_APP_LOG_GOOGLE_USAGE=true in .env to log request/response in console (cost visibility).
const LOG_GOOGLE_USAGE = process.env.VUE_APP_LOG_GOOGLE_USAGE === "true";

export function willExpireSoon(token, offset = 30) {
    if (!token) return true;
    try {
        const { exp } = jwtDecode(token); // epoch seconds
        return exp * 1000 - Date.now() < offset * 1000;
    } catch {
        return true; // malformed token
    }
}

let isRefreshing = false;
let waiting = [];
const SESSION_CANCEL = "session-expired";

function enqueue(cb) {
    waiting.push(cb);
}
function flush(newToken) {
    waiting.forEach((cb) => cb(newToken));
    waiting = [];
}

export async function refreshToken(refresh) {
    if (isRefreshing) {
        // someone else is doing it
        return new Promise((resolve) => enqueue(resolve));
    }
    isRefreshing = true;

    try {
        const { data } = await axios.post(
            `${axios.defaults.baseURL}/auth/token/refresh/`,
            { refresh }
        );
        sessionTokens.setAccessToken(data.access);
        if (data.refresh) {
            sessionTokens.setRefreshToken(data.refresh);
        }
        sessionTokens.clearLegacyJwtLocalStorage();
        axios.defaults.headers.common.Authorization = `Bearer ${data.access}`;
        flush(data.access);
        return data.access;
    } catch (e) {
        flush(null);
        throw e;
    } finally {
        isRefreshing = false;
    }
}

let loggingOut = false; // ← new global flag

export function hardLogout(reason = "tokenExpired") {
    if (loggingOut) return; // run exactly once
    loggingOut = true;

    showAlertModal(store, i18n.global.t(reason), "warning", 8000);

    sessionTokens.clearSessionTokens();
    sessionTokens.clearLegacyJwtLocalStorage();
    store.commit("auth/LOGOUT");
    delete axios.defaults.headers.common.Authorization;
    signOut(auth).catch(() => {});

    router.push({
        name: "Login",
        query: { next: router.currentRoute.value.fullPath },
    });
}

export function clearLogoutFlag() {
    loggingOut = false;
}

axios.interceptors.response.use(
    (res) => res,
    (err) => {
        if (axios.isCancel(err) && err.message === SESSION_CANCEL) {
            /* Resolve with a sentinel so awaits don’t throw
         and .catch() blocks never run.           */
            return Promise.resolve({ __sessionCancelled: true });
        }
        return Promise.reject(err); // real errors continue bubbling
    }
);

// swallow every Axios Cancel emitted while we’re logging-out
axios.interceptors.response.use(null, (err) => {
    if (axios.isCancel(err)) {
        /* do nothing – pretend the call never happened */
        return Promise.resolve(); // caller sees a resolved promise
    }
    return Promise.reject(err); // real errors keep bubbling
});

// ────────────────────────────────────────────────────────────────────────────
//  Attach Authorization header to every outgoing request (unchanged)
// ────────────────────────────────────────────────────────────────────────────
axios.interceptors.request.use(async (cfg) => {
    if (loggingOut) {
        // Cancel all in-flight requests quietly while we redirect
        return Promise.reject(new axios.Cancel(SESSION_CANCEL));
    }

    if (cfg.url?.includes("/auth/token/refresh/")) return cfg;

    let token = sessionTokens.getAccessToken();
    const refresh = sessionTokens.getRefreshToken();

    if (token && willExpireSoon(token) && refresh) {
        try {
            token = await refreshToken(refresh); // <── proactive refresh
        } catch {
            hardLogout();
        }
    }

    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// ────────────────────────────────────────────────────────────────────────────
//  Intercept 401s: clear stale token, try refresh, or retry without token on public routes
// ────────────────────────────────────────────────────────────────────────────
axios.interceptors.response.use(
    (res) => res,
    async (err) => {
        if (err.config?.url?.includes("/auth/token/refresh/")) {
            hardLogout();
            return Promise.reject(new axios.Cancel(SESSION_CANCEL));
        }

        const { response, config } = err;
        if (response?.status !== 401 || config._clearedAuth) {
            return Promise.reject(err);
        }

        const refresh = sessionTokens.getRefreshToken();
        config._clearedAuth = true;

        // Try refresh if this was a token_not_valid error and we have a refresh token
        if (response.data?.code === "token_not_valid" && refresh) {
            try {
                const newToken = await refreshToken(refresh);
                if (newToken) {
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return axios(config);
                }
            } catch {
                /* refresh failed, fall through */
            }
        }

        // Clear stale token and sync store
        sessionTokens.clearSessionTokens();
        sessionTokens.clearLegacyJwtLocalStorage();
        delete axios.defaults.headers.common.Authorization;
        store.commit("auth/LOGOUT");

        const isProtectedRoute = router.currentRoute.value?.meta?.requiresAuth;
        if (isProtectedRoute) {
            hardLogout();
            return Promise.reject(new axios.Cancel(SESSION_CANCEL));
        }

        // Public route: retry without token (AllowAny endpoints will succeed)
        delete config.headers.Authorization;
        return axios(config);
    }
);

/**
 * Fetches a list of all available countries from the API.
 *
 * @async
 * @function getCountries
 * @param {string} lang - Language code (e.g., "en", "fr", "ro").
 * @returns {Promise<Array>} - A promise that resolves to an array of country objects.
 */
export const getCountries = async (lang = "en") => {
    try {
        const response = await api.get("/countries/", { params: { lang } });
        return response.data;
    } catch (error) {
        console.error("Error fetching countries:", error);
        return [];
    }
};

/**
 * Places autocomplete. Returns { predictions: [{ place_id, description }] }.
 * @param {string} input - Search text
 * @param {string} [lang=en] - Language code
 * @param {string} [country] - Optional ISO 2 country code to bias results
 */
export const placesAutocomplete = async (input, lang = "en", country = "") => {
    const params = new URLSearchParams({ input, lang });
    if (country) params.set("country", country);
    if (LOG_GOOGLE_USAGE) {
        console.log("[GoogleCost] placesAutocomplete request", {
            input_length: (input || "").length,
            lang: lang || "en",
            country: country || "none",
        });
    }
    const { data } = await api.get(`places/autocomplete/?${params}`);
    if (LOG_GOOGLE_USAGE) {
        const count = (data && data.predictions && data.predictions.length) || 0;
        console.log("[GoogleCost] placesAutocomplete response", { suggestions_count: count, billable_requests: 1 }, data);
    }
    return data;
};

/**
 * Get Maps Embed API URL for directions iframe. mode=driving, units=metric.
 * @param {string} originPlaceId
 * @param {string} destinationPlaceId
 * @returns {Promise<{ url: string }>}
 */
export const directionsEmbedUrl = async (originPlaceId, destinationPlaceId) => {
    const params = new URLSearchParams({
        origin_place_id: originPlaceId,
        destination_place_id: destinationPlaceId,
    });
    const { data } = await api.get(`directions-embed-url/?${params}`);
    return data;
};

/**
 * Mark reservation tab as viewed (clears that part of sidebar badge).
 * @param {string} entryType - "ware" or "truck"
 * @param {string} tab - "contracted" or "received"
 */
export const markReservationTabViewed = async (entryType, tab) => {
    try {
        await api.post(`/reservations/mark-tab-viewed/?entry_type=${entryType}&tab=${tab}`);
    } catch {
        /* ignore */
    }
};

/**
 * Get reservation badge counts for sidebar (received + accepted, excluding viewed).
 * @returns {Promise<{ware_received, truck_received, ware_accepted, truck_accepted}>}
 */
export const getReservationBadgeCount = async () => {
    try {
        const { data } = await api.get("/reservations/badge-count/");
        return data;
    } catch {
        return { ware_received: 0, truck_received: 0, ware_accepted: 0, truck_accepted: 0 };
    }
};

/**
 * Unread reservation chat counts for the current user.
 * @returns {Promise<{total, ware_total, truck_total, by_reservation}>}
 */
export const getChatUnreadCount = async () => {
    try {
        const { data } = await api.get("/chat/unread-count/");
        return data;
    } catch {
        return { total: 0, ware_total: 0, truck_total: 0, by_reservation: {} };
    }
};

const api = axios;

export default api;
